/**
 * Hide and block — the PWA side of set-aside.ts on the API.
 *
 * Hiding takes a correspondence off your list until they next write (an
 * archive, not a secret: no code needed). Blocking also stops their letters
 * reaching you. Either way only your side changes; they are never told. Both
 * are undone from the profile screen's "Hidden & blocked" section, which also
 * holds refused overtures taken off the list.
 */

import { useCallback, useRef, useState } from 'react';
import { Ban, EyeOff, Info, Undo2 } from 'lucide-react';
import { useAppDispatch } from '../../store/hooks';
import { lettersApi } from '../letters/api';
import { useDialogTrap } from '../letters/useDialogTrap';
import { Avatar } from '../profiles/avatar';
import {
  useRemoveOvertureMutation,
  useRestoreSetAsideMutation,
  useSetAsideListQuery,
  useSetAsideMutation,
  type SetAsideView,
} from '../relationships/api';
import { track } from '../onboarding/track';

function errorMessage(e: unknown, fallback: string): string {
  const err = e as { data?: { message?: string | string[] } };
  const m = err?.data?.message;
  return Array.isArray(m) ? m[0] ?? fallback : m ?? fallback;
}

/** Letters follow the list: refetch them whenever a pair is set aside or back. */
function useRefreshLetters() {
  const dispatch = useAppDispatch();
  return useCallback(() => dispatch(lettersApi.util.invalidateTags(['Letter'])), [dispatch]);
}

/** Hide / block one pair, then leave the conversation (it is off the list now). */
export function useSetAsidePair(relationshipId: string, onGone: () => void) {
  const [setAside, { isLoading }] = useSetAsideMutation();
  const refresh = useRefreshLetters();
  const [error, setError] = useState<string | null>(null);
  const run = async (kind: 'hidden' | 'blocked') => {
    setError(null);
    try {
      await setAside({ relationshipId, kind }).unwrap();
      track({ event: kind === 'blocked' ? 'correspondent_blocked' : 'correspondence_set_aside' });
      refresh();
      onGone();
    } catch (e) {
      setError(errorMessage(e, kind === 'blocked' ? 'Could not block them' : 'Could not hide this conversation'));
    }
  };
  return { hide: () => run('hidden'), block: () => run('blocked'), busy: isLoading, error };
}

/** Said once, before a block is made, so nobody blocks by accident. */
export function BlockConfirm({
  peerName,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  peerName: string;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDialogTrap(ref, onCancel);
  return (
    <div className="how-dialog__backdrop" onClick={onCancel}>
      <div
        className="how-dialog block-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="block-confirm-title"
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="block-confirm__icon" aria-hidden="true">
          <Ban size={20} />
        </span>
        <h2 className="block-confirm__title" id="block-confirm-title">
          Block @{peerName}?
        </h2>
        <ul className="block-confirm__list">
          <li>Your letters with @{peerName} leave your list, your desk and your notifications.</li>
          <li>
            Anything they send from now on, and anything already in the post, is held and never
            reaches you. Unblocking later won't bring those letters back.
          </li>
          <li>They can't find you by your handle, open your invite link or send you stamp books.</li>
          <li>@{peerName} isn't told. Their letters still seal and go into the post as usual.</li>
        </ul>
        <p className="block-confirm__foot">You can unblock them any time from your profile.</p>
        {error && (
          <p className="form-error" role="alert">
            <Info size={13} aria-hidden="true" /> {error}
          </p>
        )}
        <div className="block-confirm__actions">
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn--primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Blocking…' : `Block @${peerName}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The summary page's card (phones reach hide and block here; wide screens
 * through the avatar's ⋯ menu). Not for the Post Office, which can only be hidden.
 */
export function SetAsideCard({
  relationshipId,
  peerName,
  canBlock,
  onGone,
}: {
  relationshipId: string;
  peerName: string;
  canBlock: boolean;
  onGone: () => void;
}) {
  const { hide, block, busy, error } = useSetAsidePair(relationshipId, onGone);
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="contacts-card vault-card set-aside-card">
      <div className="contacts-card__head">
        <span className="contacts-card__title">
          <EyeOff size={13} aria-hidden="true" /> Hide or block
        </span>
        <span className="contacts-card__hint">only on your side</span>
      </div>
      <p className="vault-card__copy">
        Hiding takes @{peerName} off your list until they next write.
        {canBlock && ' Blocking stops their letters reaching you at all.'} They aren't told either way.
      </p>
      <div className="set-aside-card__actions">
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => void hide()}>
          <EyeOff size={14} aria-hidden="true" /> Hide from my list
        </button>
        {canBlock && (
          <button type="button" className="btn btn--danger" disabled={busy} onClick={() => setConfirming(true)}>
            <Ban size={14} aria-hidden="true" /> Block…
          </button>
        )}
      </div>
      {error && !confirming && (
        <p className="form-error" role="alert">
          <Info size={13} aria-hidden="true" /> {error}
        </p>
      )}
      {confirming && (
        <BlockConfirm
          peerName={peerName}
          busy={busy}
          error={error}
          onConfirm={() => void block()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}

/** On a refused overture's strip: take it off the list for good (restorable). */
export function RemoveOvertureButton({ relationshipId, onGone }: { relationshipId: string; onGone: () => void }) {
  const [remove, { isLoading }] = useRemoveOvertureMutation();
  const refresh = useRefreshLetters();
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <button
        type="button"
        className="btn btn--ghost info-strip__action"
        disabled={isLoading}
        onClick={async () => {
          setError(null);
          try {
            await remove({ relationshipId }).unwrap();
            track({ event: 'overture_removed' });
            refresh();
            onGone();
          } catch (e) {
            setError(errorMessage(e, 'Could not remove it'));
          }
        }}
      >
        {isLoading ? 'Removing…' : 'Remove from list'}
      </button>
      {error && (
        <span className="form-error" role="alert">
          <Info size={13} aria-hidden="true" /> {error}
        </span>
      )}
    </>
  );
}

const KIND_LABEL: Record<SetAsideView['kind'], string> = {
  hidden: 'Hidden',
  blocked: 'Blocked',
  removed: 'Removed overtures',
};
const RESTORE_LABEL: Record<SetAsideView['kind'], string> = {
  hidden: 'Unhide',
  blocked: 'Unblock',
  removed: 'Restore',
};

function SetAsideRow({ item }: { item: SetAsideView }) {
  const [restore, { isLoading }] = useRestoreSetAsideMutation();
  const refresh = useRefreshLetters();
  const [error, setError] = useState<string | null>(null);
  const name = item.peer?.handle ?? item.peer?.displayName ?? 'someone';
  // A refused overture is known by the address it came from, not the placeholder handle.
  const label = item.kind === 'removed' && item.fromEmail ? item.fromEmail : item.peer?.handle ? `@${item.peer.handle}` : name;
  const since = new Date(item.since).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <li className="set-aside-row">
      <span className="set-aside-row__avatar">
        <Avatar name={name} avatar={item.peer?.avatar ?? null} />
      </span>
      <span className="set-aside-row__body">
        <span className="set-aside-row__name">{label}</span>
        <span className="set-aside-row__meta">{item.kind === 'removed' ? 'declined, removed' : 'since'} {since}</span>
        {error && (
          <span className="form-error" role="alert">
            <Info size={12} aria-hidden="true" /> {error}
          </span>
        )}
      </span>
      <button
        type="button"
        className="btn btn--ghost set-aside-row__action"
        disabled={isLoading}
        onClick={async () => {
          setError(null);
          try {
            await restore({ relationshipId: item.relationshipId, kind: item.kind === 'blocked' ? 'blocked' : 'hidden' }).unwrap();
            refresh();
          } catch (e) {
            setError(errorMessage(e, 'Could not bring it back'));
          }
        }}
      >
        <Undo2 size={13} aria-hidden="true" /> {RESTORE_LABEL[item.kind]}
      </button>
    </li>
  );
}

/** The profile screen's "Hidden & blocked" section. Quiet when there is nothing in it. */
export function SetAsideSection() {
  const { data: items } = useSetAsideListQuery();
  if (!items) return null;
  const groups = (['hidden', 'blocked', 'removed'] as const)
    .map((kind) => ({ kind, rows: items.filter((i) => i.kind === kind) }))
    .filter((g) => g.rows.length > 0);

  return (
    <section className="profile-fields set-aside" aria-label="Hidden and blocked">
      <h2 className="profile-fields__title">Hidden &amp; blocked</h2>
      {groups.length === 0 ? (
        <p className="field__hint">
          Nothing here. Hide or block someone from the ⋯ beside their name; only you will see a
          difference.
        </p>
      ) : (
        <>
          <p className="field__hint">
            Only on your side. Hidden letters come back when they next write; letters held while
            someone was blocked stay held after you unblock them.
          </p>
          {groups.map((g) => (
            <div key={g.kind} className="set-aside__group">
              <h3 className="set-aside__label">{KIND_LABEL[g.kind]}</h3>
              <ul className="set-aside__list">
                {g.rows.map((item) => (
                  <SetAsideRow key={item.relationshipId} item={item} />
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
